/**
 * The access token lives in memory only (never localStorage): an XSS cannot read it from storage. It is short-lived (15 min).
 * The refresh token is not visible to JavaScript at all: the server keeps it in an httpOnly cookie (SameSite=Strict, auth routes
 * only), rotates it at each exchange, and a page reload restores the session with it (`restoreSession`).
 */
type Listener = () => void;

let token: string | null = null;
let expiresAt = 0;
const listeners = new Set<Listener>();

export const session = {
  get token() { return token; },
  get expiresAt() { return expiresAt; },
  set(accessToken: string, expiresInSeconds: number) {
    token = accessToken; expiresAt = Date.now() + expiresInSeconds * 1000;
    listeners.forEach((l) => l());
  },
  clear() { token = null; expiresAt = 0; listeners.forEach((l) => l()); },
  subscribe(l: Listener) { listeners.add(l); return () => { listeners.delete(l); }; },
};

/** Called by the HTTP layer when the server says the session is over (401 that a refresh could not repair). */
let onUnauthenticated: (() => void) | null = null;
export const setUnauthenticatedHandler = (fn: () => void) => { onUnauthenticated = fn; };
export const notifyUnauthenticated = () => onUnauthenticated?.();
